import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/timer';
import 'rxjs/add/operator/takeUntil';
import { OffboardingAction, OffboardingCase, OffboardingHistory } from '../../models/offboarding.model';
import { workflowTrackBy } from '../common/employee-assets/asset.helpers';
import { OffboardingNoteViewService } from './offboarding-note-view.service';
import { OFFBOARDING as O } from './offboarding.constants';

const PREVIEW = {delay: 180, empty: '', suffix: '-notes', first: 0};
@Component({selector: 'app-offboarding-note-preview', templateUrl: './offboarding-note-preview.component.html',
  styleUrls: ['./offboarding-sequential.component.css', './offboarding-workspace-content.css']})
export class OffboardingNotePreviewComponent implements OnChanges, OnDestroy {
  @Input() row: OffboardingAction;
  @Input() item: OffboardingCase;
  @Input() events: OffboardingHistory[] = [];
  @Input() hasMore = false;
  @Input() loading = false;
  @Input() visible = true;
  @Output() more = new EventEmitter<void>();
  @ViewChild('popover') popover: NgbPopover;
  @ViewChild('trigger') trigger: ElementRef;
  @ViewChild('readingPane') readingPane: ElementRef;
  trackBy = workflowTrackBy;
  notes: OffboardingHistory[] = [];
  latest: OffboardingHistory;
  overlayId = PREVIEW.empty;
  open = false;
  private inside = false;
  private focused = false;
  private suppressFocus = false;
  private closeRequest: Subscription;
  private destroyed = new Subject<void>();
  constructor(private views: OffboardingNoteViewService) {}
  ngOnChanges(): void {
    if (!this.visible && this.popover) { this.close(false); }
    this.notes = this.events.filter(event => event.key === this.row.key && event.action === O.addNote);
    this.latest = this.row.latestNote ? this.latestNote() : this.notes[PREVIEW.first];
    if (this.latest && !this.notes.some(note => this.sameNote(note, this.latest))) { this.notes = this.notes.concat([this.latest]); }
    this.overlayId = this.row.key + PREVIEW.suffix;
  }
  private latestNote(): OffboardingHistory {
    const matching = this.notes.filter(note => note.evidence === this.row.latestNote && note.actorId === this.row.latestNoteBy);
    // The summary and history use separate server timestamps. Match without time only when every note is loaded.
    const found = matching.filter(note => note.recordedAt === this.row.latestNoteAt)[PREVIEW.first]
      || (this.row.noteCount === this.notes.length ? matching[PREVIEW.first] : null);
    return found || {id: this.row.key, key: this.row.key, action: O.addNote, evidence: this.row.latestNote,
      actorId: this.row.latestNoteBy, actorName: (this.item.actorNames || {})[this.row.latestNoteBy], recordedAt: this.row.latestNoteAt};
  }
  private sameNote(left: OffboardingHistory, right: OffboardingHistory): boolean {
    return left.recordedAt === right.recordedAt && left.evidence === right.evidence && left.actorId === right.actorId;
  }
  ngOnDestroy(): void {
    this.cancelClose(); if (this.popover) { this.views.close(this.popover); }
    this.destroyed.next(); this.destroyed.complete();
  }
  enter(): void { this.inside = true; this.cancelClose(); this.views.open(this.popover); }
  leave(): void {
    this.inside = false; this.cancelClose();
    this.closeRequest = Observable.timer(PREVIEW.delay).takeUntil(this.destroyed).subscribe(() => {
      if (!this.inside && !this.focused) { this.close(false); }
    });
  }
  focus(): void {
    this.focused = true;
    if (this.suppressFocus) { this.suppressFocus = false; return; }
    this.cancelClose(); this.views.open(this.popover);
  }
  blur(event: FocusEvent): void {
    this.focused = this.withinPane(event.relatedTarget);
    if (!this.focused) { this.leave(); }
  }
  toggle(): void { if (this.open) { this.close(false); } else { this.views.open(this.popover); } }
  read(event: KeyboardEvent): void {
    if (event.shiftKey || !this.open || !this.readingPane) { return; }
    event.preventDefault(); this.readingPane.nativeElement.focus();
  }
  @HostListener('document:keydown.escape', ['$event'])
  escape(event: KeyboardEvent): void { if (this.open) { event.stopPropagation(); this.close(true); } }
  @HostListener('document:click', ['$event'])
  outside(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.open && !this.trigger.nativeElement.contains(target) && !this.withinPane(target)) { this.close(false); }
  }
  close(restore: boolean): void {
    this.views.close(this.popover); this.cancelClose(); this.inside = false; this.focused = false;
    if (restore) { this.suppressFocus = true; this.trigger.nativeElement.focus(); this.suppressFocus = false; }
  }
  private withinPane(target: EventTarget): boolean {
    return !!target && !!this.readingPane && this.readingPane.nativeElement.contains(target as Node);
  }
  private cancelClose(): void { if (this.closeRequest) { this.closeRequest.unsubscribe(); } }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-note-preview.component.ts